import { useEffect, useRef, useState } from "react";

import { billingOnboard, previewBreakdown } from "../billingApi";
import { breakdownResultToCostBreakdown, type BreakdownResult, type CostBreakdown } from "../types";

/**
 * Server-authoritative breakdown loader, shared across submit pages.
 *
 * The drawer never displays an FE-approximated
 * breakdown. While the server hasn't responded for the CURRENT inputs,
 * `costBreakdown` is `null` (CheckoutSummary renders a skeleton) and
 * `breakdownReady` is `false` (Pay button stays disabled).
 *
 * Freshness is tracked via an input fingerprint. On coupon/subtotal change
 * the fingerprint flips and `breakdownReady` auto-flips to `false` until
 * the new preview lands. On error the last successful breakdown stays on
 * screen so the drawer doesn't blank out, but Pay stays disabled because
 * the displayed numbers no longer match the current inputs.
 *
 * Originally inlined inside `useADMSubmit.ts`; lifted here so H2H, D2D
 * and EDDM submit pages can share the same display === charge guarantee.
 */

const PREVIEW_DEBOUNCE_MS = 400;

export interface UseBillingPreviewParams {
  /** Pre-tax, pre-fee subtotal in dollars (decimal). `null` while loading. */
  subtotal: number | null | undefined;
  /** ISO-4217. Backend asserts it matches billing-profile country default. */
  currency: string;
  /** Numeric or stringified campaign id. Required for tax aggregation. */
  campaignId: string | number | null | undefined;
  /** Stripe promotion code, if applied. */
  couponCode?: string | null;
  /**
   * Gate the network call on drawer-open. Drawer-open is also the trigger
   * for the BE City Metrics sync, which depends on pathfinder data
   * populated at submit time. Firing earlier caches an empty result the
   * drawer would later reuse.
   */
  showCheckout: boolean;
  /**
   * HUB-498: when true, the server applies the client's active review discount
   * to the previewed breakdown (so the payment panel shows the discounted
   * total). Mutually exclusive with `couponCode` — one discount per order.
   */
  applyReviewDiscount?: boolean;
  /** Optional error sink (e.g. snackbar). */
  onError?: (err: Error) => void;
}

export interface UseBillingPreviewResult {
  /** Dollar-shaped breakdown for `CheckoutContent` / `CheckoutSummary`. */
  costBreakdown: CostBreakdown | null;
  /** True only when serverState matches the live input fingerprint. */
  breakdownReady: boolean;
  /** Raw cents-based breakdown (e.g. for `/charges/initiate` callers). */
  serverBreakdown: BreakdownResult | null;
}

export function useBillingPreview({
  subtotal,
  currency,
  campaignId,
  couponCode,
  applyReviewDiscount,
  showCheckout,
  onError,
}: UseBillingPreviewParams): UseBillingPreviewResult {
  // Use nullish check on subtotal — `0` is a legitimate value (e.g. a
  // 100% discount coupon) and treating it as falsy would lock Pay forever
  // for zero-cost campaigns.
  const hasValidInputs = subtotal != null && campaignId != null && campaignId !== "";
  const inputFingerprint = hasValidInputs
    ? `${subtotal}|${currency}|${couponCode || ""}|${applyReviewDiscount ? "review" : ""}|${campaignId}`
    : "";

  const [serverState, setServerState] = useState<{
    breakdown: BreakdownResult;
    fingerprint: string;
  } | null>(null);

  // Capture the latest `onError` in a ref so the preview effect can call
  // it without re-running on every render. Callers commonly pass inline
  // arrow functions; without this, either we'd re-fire the network call
  // on each render (defeating the fingerprint cache) or we'd close over a
  // stale handler. The ref is the standard React workaround until the
  // useEvent RFC ships.
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  // The breakdown endpoint 400s with BILLING_ONBOARDING_REQUIRED until the
  // user's billing profile exists. CheckoutContent calls billingOnboard()
  // from its own hook, but that fires concurrently in a sibling component,
  // so preview can win the race and fail. Awaiting an idempotent onboard
  // here (once per hook lifetime, shared via the promise ref) guarantees
  // the profile is in place before the first preview request.
  const onboardReadyRef = useRef<Promise<void> | null>(null);

  // Debounce drives the network call; the live fingerprint still gates
  // `breakdownReady`, so the Pay button reacts immediately on input change.
  const [debouncedFingerprint, setDebouncedFingerprint] = useState(inputFingerprint);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFingerprint(inputFingerprint), PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputFingerprint]);

  useEffect(() => {
    if (!showCheckout) return;
    if (serverState?.fingerprint === debouncedFingerprint) return;
    if (!hasValidInputs || !debouncedFingerprint) {
      setServerState(null);
      return;
    }
    let cancelled = false;
    const fingerprint = debouncedFingerprint;
    if (!onboardReadyRef.current) {
      onboardReadyRef.current = billingOnboard()
        .then(() => undefined)
        .catch(() => undefined);
    }
    onboardReadyRef.current
      .then(() => {
        if (cancelled) return undefined;
        return previewBreakdown({
          subtotalCents: Math.round((subtotal ?? 0) * 100),
          currency,
          entityId: String(campaignId),
          entityType: "CAMPAIGN",
          // One discount per order: review wins, coupon is only sent when no
          // review discount is being applied.
          ...(applyReviewDiscount ? { applyReviewDiscount: true } : couponCode ? { couponCode } : {}),
        });
      })
      .then((res) => {
        if (!cancelled && res) setServerState({ breakdown: res.breakdown, fingerprint });
      })
      .catch((err: Error) => {
        // eslint-disable-next-line no-console
        console.error("[useBillingPreview] previewBreakdown failed:", err);
        // Use the ref so the latest caller-supplied handler runs even
        // when the effect was scheduled with an older closure.
        if (onErrorRef.current) onErrorRef.current(err);
        // Don't clear serverState — keep last successful breakdown on
        // screen so the drawer doesn't blank out. Fingerprint mismatch
        // keeps Pay disabled until a fresh response arrives.
      });
    return () => {
      cancelled = true;
    };
    // Other inputs (subtotal, currency, campaignId, couponCode) are
    // intentionally omitted from deps because they are encoded in
    // `debouncedFingerprint`; re-running on each would defeat the cache.
    // `onError` is read via `onErrorRef` so it stays current without
    // re-firing the network call.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFingerprint, showCheckout]);

  const costBreakdown: CostBreakdown | null = serverState
    ? breakdownResultToCostBreakdown(serverState.breakdown)
    : null;
  const breakdownReady = serverState?.fingerprint === inputFingerprint;

  return {
    costBreakdown,
    breakdownReady,
    serverBreakdown: serverState?.breakdown ?? null,
  };
}
